import React, { useState, useEffect } from 'react';
import { Badge, Dropdown, List, Button, Typography, Avatar, Tag, Tooltip } from 'antd';
import { BellOutlined, UserOutlined, TeamOutlined, EyeOutlined, CloseCircleOutlined } from '@ant-design/icons';
import api from '../services/api';
import { formatDateTime } from '../utils/dateUtils';
import io from 'socket.io-client';

const { Text } = Typography;

// LocalStorage: IDs de convites/notificacoes que o user marcou como
// "ocultar" no sino. Nao remove no banco (convite continua), so tira da
// lista dessa sessao/browser. IDs armazenados como Set serializado.
const DISMISS_KEY_PREFIX = 'notif_dismissed_';
function loadDismissed(userType) {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY_PREFIX + userType) || '[]'));
  } catch { return new Set(); }
}
function saveDismissed(userType, set) {
  try {
    localStorage.setItem(DISMISS_KEY_PREFIX + userType, JSON.stringify([...set]));
  } catch { /* silencioso */ }
}

const NotificationCenter = ({ userType }) => {
  const [notifications, setNotifications] = useState([]);
  const [responseNotifications, setResponseNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(() => loadDismissed(userType));

  useEffect(() => {
    loadNotifications();
    
    // Conectar ao Socket.IO
    const getSocketURL = () => {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:8001';
      }
      return 'https://admin-api.futezap.com.br';
    };
    
    const socket = io(getSocketURL());
    
    // Identificar usuário no socket
    const userData = userType === 'player' ? 
      JSON.parse(localStorage.getItem('community_user') || '{}') :
      JSON.parse(localStorage.getItem('user') || '{}');
    
    if (userData.id) {
      socket.emit('join', { userId: userData.id, userType });
    }
    
    // Escutar novas notificações
    socket.on('new_notification', (data) => {
      console.log('Notificação recebida:', data);
      
      // Tocar som
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
      audio.volume = 0.3;
      audio.play().catch(() => {});
      
      // Se for notificação de resposta, adicionar à lista
      if (data.type === 'invite_response' || data.type === 'application_response') {
        console.log('Adicionando notificação de resposta:', data.message);
        const responseNotif = {
          id: `response_${Date.now()}`,
          type: 'response',
          message: data.message,
          timestamp: new Date()
        };
        setResponseNotifications(prev => {
          const updated = [responseNotif, ...prev.slice(0, 4)];
          // Atualizar lista principal também
          setNotifications(current => [responseNotif, ...current]);
          return updated;
        });
        setUnreadCount(prev => prev + 1);
      } else {
        // Para convites/candidaturas, recarregar notificações
        loadNotifications();
        
        // Incrementar contador apenas para novas notificações
        if (data.type === 'application' || data.type === 'invite') {
          setUnreadCount(prev => prev + 1);
        }
      }
      
      // Notificar outras páginas para atualizar
      localStorage.setItem('notification_update', Date.now());
    });
    
    // Refresh externo: qualquer pagina pode disparar 'notifications:refresh'
    // pra forcar reload (ex: apos aceitar/recusar convite pela tela de
    // convites, o contador do sino tinha que refletir imediatamente).
    const onExternalRefresh = () => loadNotifications();
    window.addEventListener('notifications:refresh', onExternalRefresh);

    return () => {
      socket.disconnect();
      window.removeEventListener('notifications:refresh', onExternalRefresh);
    };
  }, [userType]);

  const loadNotifications = async (page = 1, limit = 10) => {
    // Usar token correto baseado no tipo de usuário
    const token = userType === 'player' ? 
      localStorage.getItem('community_token') : 
      localStorage.getItem('token');
    
    console.log('NotificationCenter - Token:', token ? 'EXISTS' : 'NOT_FOUND');
    
    // Se não tiver token, não carregar notificações
    if (!token) {
      console.log('NotificationCenter - No token, skipping API call');
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    
    setLoading(true);
    try {
      
      // Carregar todas as notificações (não apenas pendentes)
      const endpoint = userType === 'player' ? 
        '/community/invites/player' : 
        '/community/invites/team';

      const response = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit }
      });

      // Para jogadores, também carregar candidaturas enviadas
      let allApiNotifications = response.data;
      if (userType === 'player') {
        const applicationsResponse = await api.get('/community/invites/player/applications', {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'ALL' }
        });
        allApiNotifications = [...response.data, ...applicationsResponse.data];
      }

      const allNotifications = page === 1 ? 
        [...responseNotifications, ...allApiNotifications] :
        [...notifications, ...allApiNotifications];
        
      setNotifications(allNotifications);
      
      if (page === 1) {
        // Contar apenas pendentes para badge
        const pendingCount = allApiNotifications.filter(n => n.status === 'PENDING').length + responseNotifications.length;
        setUnreadCount(pendingCount);
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    }
    setLoading(false);
  };

  const handleResponse = async (inviteId, status) => {
    try {
      // Usar token correto baseado no tipo de usuário
      const token = userType === 'player' ? 
        localStorage.getItem('community_token') : 
        localStorage.getItem('token');

      await api.put(`/community/invites/${inviteId}/respond`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Atualizar status local sem recarregar
      setNotifications(prev => prev.map(n => 
        n.id === inviteId ? { ...n, status } : n
      ));
      
      // Diminuir contador de não lidas
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Notificar outras páginas para atualizar
      localStorage.setItem('notification_update', Date.now());
    } catch (error) {
      console.error('Erro ao responder convite:', error);
    }
  };

  const renderNotificationItem = (item) => {
    const isPlayerNotification = userType === 'player';
    
    // Notificações de resposta
    if (item.type === 'response') {
      return (
        <List.Item style={{ 
          padding: window.innerWidth < 768 ? '8px 8px' : '12px 20px',
          backgroundColor: '#f6ffed' 
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%', 
            gap: window.innerWidth < 768 ? 12 : 16 
          }}>
            <Avatar 
              icon={<UserOutlined />} 
              style={{ backgroundColor: '#52c41a' }}
              size={window.innerWidth < 768 ? 'small' : 'default'}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontSize: window.innerWidth < 768 ? 11 : 12, 
                color: '#666', 
                lineHeight: 1.4 
              }}>
                {item.message}
              </div>
              <div style={{ 
                fontSize: window.innerWidth < 768 ? 9 : 10, 
                color: '#999' 
              }}>
                {item.timestamp?.toLocaleTimeString()}
              </div>
            </div>
          </div>
        </List.Item>
      );
    }
    
    // Notificações de convites/candidaturas.
    // Direcao real (independente do userType do widget):
    //  - invited_by_team=true  -> time convidou jogador
    //  - invited_by_team=false -> jogador se candidatou pro time
    // O NOME que aparece no topo eh SEMPRE o do outro lado da conversa:
    //  - time convidou jogador: player ve nome do TIME
    //  - jogador se candidatou: dono do time ve nome do JOGADOR
    const isCandidatura = !item.invited_by_team;
    const title = isCandidatura ? item.player_name : item.team_name;
    const subtitle = isCandidatura
      ? (item.game_date
          ? `Candidatura recebida para o jogo ${item.game_date}`
          : 'Candidatura recebida pra fazer parte do time')
      : (item.game_date
          ? `Convite para jogar em ${item.game_date}`
          : 'Convite para fazer parte do time');

    // Player: responde convites recebidos E candidaturas em times dele
    // Organizer: responde candidaturas recebidas
    const canRespond = item.status === 'PENDING';
    
    // Status visual
    const getStatusText = () => {
      if (item.status === 'ACCEPTED') return '✓ Aceito';
      if (item.status === 'REJECTED') return '✕ Recusado';
      return '';
    };

    return (
      <List.Item style={{ 
        padding: window.innerWidth < 768 ? '8px 8px' : '12px 20px',
        backgroundColor: item.status === 'PENDING' ? 'white' : '#f9f9f9'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%', 
          gap: window.innerWidth < 768 ? 12 : 16 
        }}>
          <Avatar 
            icon={isPlayerNotification ? <TeamOutlined /> : <UserOutlined />}
            size={window.innerWidth < 768 ? 'small' : 'default'}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: 500, 
              marginBottom: 4,
              fontSize: window.innerWidth < 768 ? 13 : 14
            }}>
              {title}
            </div>
            <div style={{ 
              fontSize: window.innerWidth < 768 ? 11 : 12, 
              color: '#666', 
              lineHeight: 1.4 
            }}>
              {subtitle}
            </div>
            {item.status !== 'PENDING' && (
              <div style={{ 
                fontSize: window.innerWidth < 768 ? 10 : 11, 
                color: item.status === 'ACCEPTED' ? '#52c41a' : '#ff4d4f', 
                marginTop: 2 
              }}>
                {getStatusText()}
              </div>
            )}
          </div>
          <div style={{
            display: 'flex',
            gap: window.innerWidth < 768 ? 4 : 6,
            flexShrink: 0,
            alignItems: 'center',
          }}>
            {canRespond && (
              <>
                <Button
                  size="small" type="primary"
                  onClick={() => handleResponse(item.id, 'ACCEPTED')}
                  style={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                >✓</Button>
                <Button
                  size="small"
                  onClick={() => handleResponse(item.id, 'REJECTED')}
                  style={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                >✕</Button>
              </>
            )}
            <Tooltip title="Abrir convite">
              <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => handleOpen(item)} />
            </Tooltip>
            <Tooltip title="Ocultar">
              <Button size="small" type="text" icon={<CloseCircleOutlined />} onClick={() => handleDismiss(item.id)} />
            </Tooltip>
          </div>
        </div>
      </List.Item>
    );
  };

  // Abrir: navega pra pagina certa conforme direcao (nao userType), pra que
  // dono do time comunidade caia em /time/convites ao clicar em candidatura,
  // mesmo estando com widget montado com userType='player'.
  const handleOpen = (item) => {
    const isCandidatura = !item.invited_by_team;
    let targetPath;
    if (userType === 'player') {
      targetPath = isCandidatura ? '/time/convites' : '/player/invites';
    } else {
      targetPath = '/community/invites';
    }
    if (window.location.pathname === targetPath) {
      window.dispatchEvent(new CustomEvent('convites:open-drawer', { detail: { id: item.id } }));
    } else {
      window.location.href = `${targetPath}?invite_id=${item.id}`;
    }
  };

  // Ocultar: adiciona ao Set local. Salva no localStorage.
  const handleDismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(userType, next);
  };

  const handleScroll = (e) => {
    const { target } = e;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
      const currentPage = Math.ceil(notifications.length / 10) + 1;
      loadNotifications(currentPage, 10);
    }
  };

  const dropdownContent = (
    <div style={{ 
      width: window.innerWidth < 768 ? Math.min(280, window.innerWidth - 16) : 380, 
      maxHeight: window.innerWidth < 768 ? 300 : 400, 
      overflow: 'auto',
      backgroundColor: 'white',
      border: '1px solid #d9d9d9',
      borderRadius: '6px',
      boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08)',
      position: 'relative',
      right: window.innerWidth < 768 ? 0 : 'auto',
      paddingLeft: window.innerWidth < 768 ? '8px' : '0px'
    }}
    onScroll={handleScroll}
    >
      <div style={{ 
        padding: '12px 16px', 
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: 'white'
      }}>
        <Text strong>Notificações</Text>
      </div>
      <List
        loading={loading}
        dataSource={notifications.filter(n => !dismissed.has(n.id))}
        renderItem={renderNotificationItem}
        locale={{ emptyText: 'Nenhuma notificação' }}
        style={{ backgroundColor: 'white' }}
      />
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      placement={window.innerWidth < 768 ? 'bottomLeft' : 'bottomRight'}
      getPopupContainer={() => document.body}
      overlayStyle={{ zIndex: 1500 }}
    >
      <Badge count={unreadCount} size="small">
        <Button 
          type="text" 
          icon={<BellOutlined style={{fontSize: '18px'}} />}
          style={{ 
            border: 'none',
            color: userType === 'organizer' ? 'white' : 'inherit'
          }}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationCenter;